import React from "react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { CSS } from "@dnd-kit/utilities";
import CreateIcon from "@mui/icons-material/Create";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { CloseOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../../sdk";

// Sortable RatePlan Item Component
const SortableRatePlanItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
    opacity: isDragging ? 0.5 : 1,
    borderRadius: "8px",
  };

  return (
    <RatePlanStack ref={setNodeRef} style={style}>
      <DragIndicatorIcon
        {...attributes}
        {...listeners}
        sx={{ cursor: "grab", color: "gray" }}
      />
      <Stack flex={1}>{children}</Stack>
    </RatePlanStack>
  );
};

const ResavenueRatePlanMapping = ({
  room,
  roomTypes,
  roomIndex,
  setRoomsData,
  addNewRatePlan,
  newRatePlanData,
  setAddNewRatePlan,
  handleNewRatePlanChange,
  createRoomOrRatePlan,
  updateRatePlan,
  handleEditRateplan,
  isRatePlanDataIncomplete,
  changeHandlerRatePlan,
  changeRatePlanSequence,
  fetchRooms,
  setSnackBarMsg,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const { authFetch, hotelSelect, permission } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [ratePlanToDelete, setRatePlanToDelete] = React.useState(null);

  const hasDeleteRoomRatePlanPermission = permission?.some(
    (p) => p.name === "deleteRatePlan"
  );

  const handleDeleteRoomRatePlan = async (roomRatePlanId) => {
    if (!hotelSelect?.id || !roomRatePlanId) return;

    try {
      const { del } = await authFetch({
        path: `/hotel/${hotelSelect.id}/room-rate-plan/${roomRatePlanId}`,
      });

      const { response } = await del();

      if (response.ok) {
        if (fetchRooms) {
          fetchRooms();
        }
      }
    } catch (err) {
      console.error("Error deleting room rate plan:", err);
      if (setSnackBarMsg) {
        setSnackBarMsg({
          open: true,
          msg: "An error occurred while deleting. Please try again.",
          color: "error",
        });
      }
    }
  };

  // Dialog handlers
  const openDeleteDialog = (roomRatePlanId) => {
    setRatePlanToDelete(roomRatePlanId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRatePlanToDelete(null);
  };

  const confirmDelete = async () => {
    if (ratePlanToDelete) {
      await handleDeleteRoomRatePlan(ratePlanToDelete);
      closeDeleteDialog();
    }
  };

  // Handle rate plan reordering
  const handleRatePlanDragEnd = (roomId, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setRoomsData((prev) =>
      prev.map((room) => {
        if (room.roomId !== roomId) return room;

        const oldIndex = room.ratePlans.findIndex(
          (p) => p.RoomRatePlanId === active.id
        );
        const newIndex = room.ratePlans.findIndex(
          (p) => p.RoomRatePlanId === over.id
        );

        const selectedRatePlan = room.ratePlans[oldIndex];
        const movedToRatePlan = room.ratePlans[newIndex];
        const ratePlanId = selectedRatePlan?.RoomRatePlanId;
        const formerSequence = selectedRatePlan?.sequence;
        const laterSequence = movedToRatePlan?.sequence;

        changeRatePlanSequence(ratePlanId, formerSequence, laterSequence);

        return {
          ...room,
          ratePlans: arrayMove(room.ratePlans, oldIndex, newIndex),
        };
      })
    );
  };

  return (
    <Stack spacing={1}>
      <RatePlanHeader>
        <Typography className="header-title">Rate Plans</Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => setAddNewRatePlan(roomIndex)}
        >
          + Add Rate Plan
        </Button>
      </RatePlanHeader>

      {/* Add New RatePlan Data */}
      {addNewRatePlan === roomIndex && (
        <AddNewRatePlanStack>
          <TextField
            label="Rate Plan Id"
            value={newRatePlanData.roomRateGroupCode || ""}
            onChange={(e) =>
              handleNewRatePlanChange("roomRateGroupCode", e.target.value)
            }
          />
          <TextField
            label="Rate Plan Name"
            value={newRatePlanData.roomRatePlanName || ""}
            onChange={(e) =>
              handleNewRatePlanChange("roomRatePlanName", e.target.value)
            }
          />
          <TextField
            label="Promo Discount (%)"
            type="number"
            value={newRatePlanData.promotionalDiscount ?? ""}
            onChange={(e) =>
              handleNewRatePlanChange(
                "promotionalDiscount",
                e.target.value ? Number(e.target.value) : null
              )
            }
          />
          <Autocomplete
            value={
              roomTypes.find(
                (rt) => rt.value === newRatePlanData.occupancyType
              ) || null
            }
            onChange={(_, newValue) =>
              handleNewRatePlanChange(
                "occupancyType",
                newValue ? newValue.value : null
              )
            }
            options={roomTypes}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField {...params} label="Occupancy Type" />
            )}
            sx={{ minWidth: 200 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newRatePlanData.isBaseRoom || false}
                onChange={() =>
                  handleNewRatePlanChange(
                    "isBaseRoom",
                    !newRatePlanData.isBaseRoom
                  )
                }
              />
            }
            label={"Is Base Rate Plan"}
          />

          <Stack className="rateplan-actions">
            <Button
              variant="contained"
              disabled={isRatePlanDataIncomplete}
              onClick={() => {
                createRoomOrRatePlan(false, room, newRatePlanData);
              }}
            >
              Save
            </Button>
            <IconButton onClick={() => setAddNewRatePlan(false)}>
              <CloseOutlined />
            </IconButton>
          </Stack>
        </AddNewRatePlanStack>
      )}

      <RatePlanWrapper>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => handleRatePlanDragEnd(room.roomId, e)}
        >
          <SortableContext
            items={room.ratePlans.map((p) => p.RoomRatePlanId)}
            strategy={verticalListSortingStrategy}
          >
            {room.ratePlans.map((plan, ratePlanIndex) => (
              <SortableRatePlanItem
                key={plan.RoomRatePlanId}
                id={plan.RoomRatePlanId}
              >
                {!plan.isEditRatePlan ? (
                  // View Mode
                  <SingleRatePlanStack>
                    <Stack className="rateplan-item">
                      <Typography className="rateplan-label">
                        Rate Plan:
                      </Typography>
                      <Typography className="rateplan-value">
                        {`${plan.roomRatePlanName} (ID: ${plan.roomRateGroupCode})`}
                      </Typography>

                      {plan.isBaseRoom && (
                        <Stack className="base-rate">Base Rate</Stack>
                      )}
                    </Stack>

                    <Stack className="rateplan-item">
                      <Typography className="rateplan-label">
                        Promo Discount:
                      </Typography>
                      <Typography className="rateplan-value">
                        {plan.promotionalDiscount ?? 0}%
                      </Typography>
                    </Stack>

                    <Stack className="rateplan-item">
                      <Typography className="rateplan-label">
                        Occupancy:
                      </Typography>
                      <Typography className="rateplan-value">
                        {plan.occupancyType}
                      </Typography>
                    </Stack>

                    <Stack className="rateplan-item">
                      <Typography className="rateplan-label">
                        Base Rate:
                      </Typography>
                      <Typography className="rateplan-value">
                        {plan.isBaseRoom ? "Yes" : "No"}
                      </Typography>
                    </Stack>

                    <Stack className="rateplan-actions">
                      <IconButton
                        onClick={() =>
                          handleEditRateplan(roomIndex, ratePlanIndex)
                        }
                      >
                        <CreateIcon />
                      </IconButton>
                      {hasDeleteRoomRatePlanPermission && (
                        <IconButton
                          onClick={() => openDeleteDialog(plan.RoomRatePlanId)}
                        >
                          <DeleteIcon sx={{ color: "#306FBC" }} />
                        </IconButton>
                      )}
                    </Stack>
                  </SingleRatePlanStack>
                ) : (
                  // Edit Mode
                  <SingleRatePlanStack>
                    <TextField
                      label="Rate Plan Name"
                      value={plan.roomRatePlanName || ""}
                      onChange={(e) =>
                        changeHandlerRatePlan(
                          roomIndex,
                          ratePlanIndex,
                          "roomRatePlanName",
                          e.target.value
                        )
                      }
                    />
                    <TextField
                      label="Promo Discount (%)"
                      type="number"
                      value={plan.promotionalDiscount ?? ""}
                      onChange={(e) =>
                        changeHandlerRatePlan(
                          roomIndex,
                          ratePlanIndex,
                          "promotionalDiscount",
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    />
                    <Autocomplete
                      value={
                        roomTypes.find(
                          (rt) => rt.value === plan.occupancyType
                        ) || null
                      }
                      onChange={(_, newValue) =>
                        changeHandlerRatePlan(
                          roomIndex,
                          ratePlanIndex,
                          "occupancyType",
                          newValue ? newValue.value : null
                        )
                      }
                      options={roomTypes}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Occupancy Type" />
                      )}
                      sx={{ minWidth: 200 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={plan.isBaseRoom || false}
                          onChange={() =>
                            changeHandlerRatePlan(
                              roomIndex,
                              ratePlanIndex,
                              "isBaseRoom",
                              !plan.isBaseRoom
                            )
                          }
                        />
                      }
                      label={"Is Base Rate Plan"}
                    />

                    <Stack className="rateplan-actions">
                      <Button
                        variant="contained"
                        onClick={() =>
                          updateRatePlan(plan, roomIndex, ratePlanIndex)
                        }
                      >
                        Save
                      </Button>
                      <IconButton
                        onClick={() =>
                          handleEditRateplan(roomIndex, ratePlanIndex)
                        }
                      >
                        <CloseOutlined />
                      </IconButton>
                    </Stack>
                  </SingleRatePlanStack>
                )}
              </SortableRatePlanItem>
            ))}
          </SortableContext>
        </DndContext>
      </RatePlanWrapper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Rate Plan?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this rate plan?
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ResavenueRatePlanMapping;

// Styled Components
const RatePlanStack = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(1),
  padding: "16px 0px 16px 2px",
  border: `1px solid #E2E8F0`,
  background: "#F8FAFC4D",
}));

const RatePlanHeader = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  ".header-title": {
    fontSize: theme.spacing(2),
    fontWeight: 500,
  },
}));

const RatePlanWrapper = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

const AddNewRatePlanStack = styled(Stack)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2.5),
  background: "#F8FAFC4D",
  border: `1px solid #E2E8F0`,
  borderRadius: theme.spacing(1),
  ".rateplan-actions": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
}));

const SingleRatePlanStack = styled(Stack)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  ".rateplan-item": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    width: 300,
    overflow: "hidden",
  },
  ".rateplan-label": {
    color: "#64748B",
    fontSize: theme.spacing(1.75),
    fontWeight: 500,
  },
  ".rateplan-value": {
    fontSize: theme.spacing(2),
    fontWeight: 600,
    textTransform: "capitalize",
  },
  ".rateplan-actions": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  ".base-rate": {
    background: "#163A90",
    color: "#ffffff",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    fontSize: theme.spacing(1.5),
    fontWeight: 500,
  },
}));
